import Big from 'big.js';

import { BUY, DIRECTIONS } from 'src/util/constants';

/**
 * Sentinel key used for the aggregated "Other" series in filtered chart data.
 * Guaranteed not to collide with real trade/meter keys because
 * `buildTradeViewKey` always produces patterns like
 * `parentId_TRADE_TYPE_..._DATA_AGGREGATE_...`, and meter UUIDs are standard UUIDs.
 */
export const OTHER_KEY = '__other__';

/**
 * Coerces a value to a finite number, returning 0 for null, undefined, or
 * non-numeric strings that would otherwise cause Big() to throw.
 * @param {*} v - The value to coerce.
 * @returns {number} A finite number.
 */
const safeNum = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

/**
 * A single data point within a trade series.
 * The fields `value`, `volume`, and `carbon` are summed across hidden series
 * when building the "Other" bucket. `interval` is copied from the first hidden
 * series that writes a given timestamp.
 *
 * **Contract:** adding a new summable field requires updating
 * `getFilteredTradeChartDataByVisibleKeys` and every producer that constructs
 * trade point objects (e.g. `buildTradeViewChartData`). Failing to do so
 * silently drops the field from the "Other" aggregate.
 * @typedef {object} TradePoint
 * @property {{ timestamp: string, length: string }} interval - ISO 8601 interval descriptor.
 * @property {number} value - Energy traded in the active unit.
 * @property {number} volume - Volume traded.
 * @property {number} carbon - Carbon equivalent.
 */

/**
 * A single data point within a meter (historian) direction bucket.
 * The fields `value` and `carbon` are summed across hidden meters when
 * building the "Other" bucket. `timestamp` is copied from the first hidden
 * meter that writes a given timestamp. `flags` are union-merged across all
 * contributing hidden meters so data-quality markers are preserved.
 *
 * **Contract:** adding a new summable field requires updating
 * `getFilteredMeterChartDataByVisibleKeys` and every producer that constructs
 * meter point objects (e.g. `buildMeterViewChartData`). Failing to do so
 * silently drops the field from the "Other" aggregate.
 * @typedef {object} MeterPoint
 * @property {string} timestamp - ISO 8601 timestamp string.
 * @property {number} value - Energy measured in the active unit.
 * @property {Array<{identifier: string, description: string}>} flags - Data-quality flag objects.
 * @property {number|import('big.js').Big} carbon - Carbon equivalent; may arrive as a
 *   Big instance from `buildMeterViewChartData` and is converted to a plain Number on output.
 */

/**
 * Union-merges two flag arrays without duplicates, deduplicating by `identifier`
 * for object flags (e.g. `{ identifier, description }`) or by value for string flags.
 * This is necessary because flag objects from different meters are distinct references
 * even when they share the same identifier, so `new Set()` cannot remove them.
 * @param {Array} existing - Accumulated flags already in the target point.
 * @param {Array} incoming - Flags from the current meter point being merged.
 * @returns {Array} Deduplicated union of both arrays.
 */
const mergeFlags = (existing, incoming) => {
  const seen = new Map(existing.map((f) => [f?.identifier ?? f, f]));
  (incoming ?? []).forEach((f) => {
    const key = f?.identifier ?? f;
    if (!seen.has(key)) seen.set(key, f);
  });
  return [...seen.values()];
};

/**
 * Filters consolidated trade chart data so only the series whose keys are in
 * `visibleKeys` are kept. All non-visible series are merged into a single
 * `__other__` series by summing `value`, `volume`, and `carbon` at each timestamp.
 * Returns the original `chartData` reference only when `visibleKeys` is falsy or
 * empty. When every series is already visible, a new `{ buy, sell }` object is
 * returned containing the same series (no "Other" bucket is added).
 * @param {object} chartData - Consolidated trade chart data `{ buy: [...], sell: [...] }`.
 * @param {Set<string>} visibleKeys - Set of card keys that are currently visible.
 * @param {string} otherLabel - Translated label for the aggregated "Other" series.
 * @returns {{ buy: Array, sell: Array }} Filtered chart data.
 */
export const getFilteredTradeChartDataByVisibleKeys = (chartData, visibleKeys, otherLabel) => {
  if (!visibleKeys || visibleKeys.size === 0) {
    return chartData;
  }

  const result = {};

  DIRECTIONS.forEach((dir) => {
    const series = chartData[dir];
    if (!series || series.length === 0) {
      result[dir] = series || [];
      return;
    }

    const visibleSeries = [];
    const hiddenSeries = [];

    series.forEach((s) => {
      if (visibleKeys.has(s.key)) {
        visibleSeries.push(s);
      } else {
        hiddenSeries.push(s);
      }
    });

    if (hiddenSeries.length === 0) {
      result[dir] = visibleSeries;
      return;
    }

    // Merge all hidden series into a single __other__ entry.
    // Big-based summation guards against floating-point accumulation errors and
    // string concatenation if any field arrives as a non-numeric string.
    const otherAccum = {};
    hiddenSeries.forEach((s) => {
      Object.entries(s.data).forEach(([ts, point]) => {
        if (!otherAccum[ts]) {
          otherAccum[ts] = {
            interval: point.interval,
            value: Big(0),
            volume: Big(0),
            carbon: Big(0),
          };
        }
        otherAccum[ts].value = otherAccum[ts].value.plus(safeNum(point.value));
        otherAccum[ts].volume = otherAccum[ts].volume.plus(safeNum(point.volume));
        otherAccum[ts].carbon = otherAccum[ts].carbon.plus(safeNum(point.carbon));
      });
    });

    const otherData = Object.fromEntries(
      Object.entries(otherAccum).map(([ts, point]) => [ts, {
        interval: point.interval,
        value: Number(point.value),
        volume: Number(point.volume),
        carbon: Number(point.carbon),
      }]),
    );

    const otherSeries = {
      key: OTHER_KEY,
      label: otherLabel,
      subLabel: null,
      user: null,
      property: null,
      meter: null,
      tradeType: null,
      data: otherData,
    };

    result[dir] = [...visibleSeries, otherSeries];
  });

  return result;
};

/**
 * Filters meter chart data so only the meter entries whose keys are in
 * `visibleKeys` are kept. All non-visible entries are merged into a single
 * `__other__` entry by summing `value` and `carbon` at each timestamp.
 * Returns the original `chartData` reference only when `visibleKeys` is falsy or
 * empty. When every entry is already visible, a new object is returned
 * containing the same entries (no "Other" entry is added).
 * @param {object} chartData - Meter chart data `{ meterId: { buy, sell, title, identifier } }`.
 * @param {Set<string>} visibleKeys - Set of meter keys currently visible.
 * @param {string} otherLabel - Translated label for the aggregated "Other" entry.
 * @returns {object} Filtered meter chart data.
 */
export const getFilteredMeterChartDataByVisibleKeys = (chartData, visibleKeys, otherLabel) => {
  if (!visibleKeys || visibleKeys.size === 0) {
    return chartData;
  }

  const visibleEntries = {};
  const hiddenKeys = [];

  Object.keys(chartData).forEach((key) => {
    if (visibleKeys.has(key)) {
      visibleEntries[key] = chartData[key];
    } else {
      hiddenKeys.push(key);
    }
  });

  if (hiddenKeys.length === 0) {
    return visibleEntries;
  }

  // Merge all hidden meter entries into a single __other__ entry
  const otherBuy = {};
  const otherSell = {};

  hiddenKeys.forEach((key) => {
    const entry = chartData[key];

    DIRECTIONS.forEach((dir) => {
      const target = dir === BUY ? otherBuy : otherSell;
      const dirData = entry[dir] || {};

      Object.entries(dirData).forEach(([ts, point]) => {
        if (!target[ts]) {
          target[ts] = {
            timestamp: point.timestamp,
            value: Big(0),
            flags: [],
            carbon: Big(0),
          };
        }
        // Union-merge flags so data-quality markers from all hidden meters are preserved.
        target[ts].flags = mergeFlags(target[ts].flags, point.flags);
        // safeNum guards against non-numeric strings that would cause Big() to throw.
        target[ts].value = target[ts].value.plus(safeNum(point.value));
        target[ts].carbon = target[ts].carbon.plus(safeNum(point.carbon));
      });
    });
  });

  const getNumericDirData = (dirData) => Object.fromEntries(
    Object.entries(dirData).map(([ts, point]) => [ts, {
      timestamp: point.timestamp,
      value: Number(point.value),
      flags: point.flags,
      carbon: Number(point.carbon),
    }]),
  );

  return {
    ...visibleEntries,
    [OTHER_KEY]: {
      buy: getNumericDirData(otherBuy),
      sell: getNumericDirData(otherSell),
      title: otherLabel,
      identifier: '',
    },
  };
};
